/**
 * Analytics - Channel Events
 * 
 * @atomic - Channel/messaging tracking
 * @modular - Channel-specific events
 * 
 * Purpose: Track channel interactions and messaging
 * Exports: trackChannelCreated, trackMessageSent, etc.
 */

import { trackEvent } from './core'
import { createLogger } from '../logger'

const logger = createLogger('ChannelEvents')

/**
 * Helper to track first-time actions
 * @private
 */
const trackFirstTimeAction = (flagKey, category, action, label, params = {}) => {
  const localStorageKey = `analytics_first_${flagKey}`
  
  if (!localStorage.getItem(localStorageKey)) {
    trackEvent(category, action, label, null, {
      ...params,
      is_first_time: true,
      timestamp: new Date().toISOString(),
    })
    localStorage.setItem(localStorageKey, 'true')
    logger.info(`First-time ${action} tracked`, params)
  }
}

/**
 * Track channel created
 */
export const trackChannelCreated = (channelType, entityId = null) => {
  trackEvent('Channels', 'channel_created', channelType, null, {
    entity_id: entityId
  })
}

/**
 * Track message sent
 */
export const trackMessageSent = (channelId, messageLength, hasImages = false) => {
  trackEvent('Channels', 'message_sent', null, messageLength, {
    channel_id: channelId,
    has_images: hasImages
  })
  
  // Check for first message (activation milestone)
  trackFirstTimeAction('channel_message', 'Channels', 'first_message_sent', null, { 
    channel_id: channelId,
    message_length: messageLength,
    has_images: hasImages 
  })
}

/**
 * Track channel opened
 */
export const trackChannelOpened = (channelId, channelType, source = 'list') => {
  trackEvent('Channels', 'channel_opened', channelType, null, {
    channel_id: channelId,
    source: source
  })
}

/**
 * Track messages read
 */
export const trackMessagesRead = (channelId, unreadCount) => {
  trackEvent('Channels', 'messages_read', null, unreadCount, {
    channel_id: channelId
  })
}

/**
 * Track channel message read (alias for trackMessagesRead)
 */
export const trackChannelMessageRead = (channelId, messageCount) => {
  trackEvent('Channels', 'message_read', null, messageCount, {
    channel_id: channelId
  })
}

/**
 * Track contact form submission
 */
export const trackContactFormSubmit = (isVendor, hasSubject, source, messageLength) => {
  trackEvent('Contact', 'form_submit', source, messageLength, {
    is_vendor: isVendor,
    has_subject: hasSubject
  })
}

/**
 * Track contact form field interaction
 */
export const trackContactFormFieldInteraction = (fieldName, action, metadata = {}) => {
  trackEvent('Contact', `field_${action}`, fieldName, null, metadata)
}

/**
 * Track contact form vendor toggle
 */
export const trackContactFormVendorToggle = (isChecked, source = 'contact_form') => {
  trackEvent('Contact', 'vendor_toggle', source, isChecked ? 1 : 0)
}

/**
 * Track channel activity section expanded
 */
export const trackChannelActivityView = () => {
  trackEvent('Channels', 'channel_activity_viewed', 'activity_tab')
}

/**
 * Track channel activity item clicked (navigates to channel)
 */
export const trackChannelActivityClick = (channelId, entityId) => {
  trackEvent('Channels', 'channel_activity_clicked', null, null, {
    channel_id: channelId,
    entity_id: entityId
  })
}

/**
 * Track contact form progression
 */
export const trackContactFormProgression = (fieldStates, completionPercentage) => {
  trackEvent('Contact', 'form_progression', null, completionPercentage, {
    field_states: fieldStates
  })
}

/**
 * Track channel notification click
 */
export const trackChannelNotificationClick = (channelId, notificationSource = 'desktop') => {
  trackEvent('Channels', 'notification_click', channelId, null, {
    channel_id: channelId,
    notification_source: notificationSource
  })
}

/**
 * Track channel rate limit hit
 */
export const trackChannelRateLimitHit = (channelId, limitType, minutesRemaining) => {
  trackEvent('Channels', 'rate_limit_hit', limitType, null, {
    channel_id: channelId,
    limit_type: limitType,
    minutes_remaining: minutesRemaining
  })
}

/**
 * Track admin action
 */
export const trackAdminAction = (action, resourceType, resourceId, metadata = {}) => {
  trackEvent('Admin', action, resourceType, null, {
    resource_type: resourceType,
    resource_id: resourceId,
    ...metadata
  })
}

/**
 * Track admin entity verification
 */
export const trackAdminEntityVerify = (entityId, entityName, verificationStatus) => {
  trackAdminAction('entity_verify', 'entity', entityId, {
    entity_name: entityName,
    verification_status: verificationStatus
  })
}

/**
 * Track admin sighting moderation
 */
export const trackAdminSightingModerate = (sightingId, action, hadImages) => {
  trackAdminAction('sighting_moderate', 'sighting', sightingId, {
    moderation_action: action,
    had_images: hadImages
  })
}

/**
 * Track admin contact status update
 */
export const trackAdminContactUpdate = (contactId, newStatus, oldStatus) => {
  trackAdminAction('contact_update', 'contact', contactId, {
    new_status: newStatus,
    old_status: oldStatus
  })
}

/**
 * Track admin user action
 */
export const trackAdminUserAction = (userId, action, metadata = {}) => {
  trackAdminAction(`user_${action}`, 'user', userId, metadata)
}
